import React, { useEffect, useState, useContext } from 'react';
import { AppContext } from "../../util/AppContext";
import { getConfigValue } from "../../util/getConfigValue";
import PropType from 'prop-types';
import { getAnnoucements } from "../../API/announcements";
import DOMPurify from 'dompurify';
import truncate from 'truncate-html';
import 'react-quill/dist/quill.snow.css'
import { useErrorBoundary } from "react-error-boundary";
import { Carousel, Container, Row, Col } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';

AnouncementsCard.propTypes = {
    filters: PropType.object
};

const AnnouncementSlide = ({  productName, content, maxLength, readMoreText, onReadMore }) => {
    let processedContent = content;

    if(productName) {
        const pTagRegex = /<p.*?>/;
        if (pTagRegex.test(processedContent)) {
            processedContent = processedContent.replace(pTagRegex, `$&<strong>${productName}:</strong> `);
        } else {
            processedContent = `<strong>${productName}:</strong> ${processedContent}`;
        }
    }

    const textOnly = processedContent.replace(/<[^>]+>/g, '');
    const needsTruncation = textOnly.length > maxLength;

    if(needsTruncation) {
        const truncatedContent = truncate(processedContent, maxLength, { ellipsis: '...' });
        return (
            <div className="ql-editor d-flex flex-column align-items-center justify-content-center pb-4">
                <span dangerouslySetInnerHTML={{ __html: truncatedContent }} />
                <button className="btn btn-link" onClick={(e) => { e.preventDefault(); onReadMore(); }}>{readMoreText}</button>
            </div>
        );
    }

    return <div className="ql-editor d-flex flex-column align-items-center justify-content-center" dangerouslySetInnerHTML={{ __html: processedContent }}></div>;
};

const AnnouncementModal = ({  show, setShow, productName, content, setIsPaused }) => {
    const handleClose = () => {
        setShow(false);
        setIsPaused(false);
    };

	return (
		<Modal className='modal-lg' show={show} onHide={handleClose}>
			<Modal.Header className={`modal-header`} closeButton>
                <Modal.Title className='modal-title fs-5'>{productName}</Modal.Title>
			</Modal.Header>
			<Modal.Body className='modal-body'>
                <div className="ql-editor" dangerouslySetInnerHTML={{ __html: content }} />
			</Modal.Body>
		</Modal>
	);
}

AnnouncementSlide.propTypes = {
    productName: PropType.string,
    content: PropType.string,
    maxLength: PropType.number,
    readMoreText: PropType.string,
    onReadMore: PropType.func
};

AnnouncementModal.propTypes = {
    productName: PropType.string,
    content: PropType.string,
    show: PropType.bool,
    setShow: PropType.func,
    setIsPaused: PropType.func
};

function AnouncementsCard({ filters }) {
    const [appState] = useContext(AppContext);
    const [isLoading, setIsLoading] = useState(true);
    const cardEnabled = getConfigValue(appState?.config?.Leaderboard?.Charts?.Announcements?.Enabled, appState)?.Value === 'true' || false;
    const [announcementsEnabled, setAnnouncementsEnabled] = useState(false);
    const slideTransitionTime = (getConfigValue(appState?.config?.AnnouncementManagement?.Carousel?.AutoSlideTime, appState)?.Value || 5) * 1000;
    const [announcements, setAnnouncements] = useState([]);
    const announcmentPartition = "Dashboard";
    const announcementAllBrandLabel = getConfigValue(appState?.config?.AnnouncementManagement?.Announcement?.AllBrands, appState)?.Value;
    const maximumCharacterCount = parseInt(getConfigValue(appState?.config?.AnnouncementManagement?.Carousel?.MaxCharacterCount, appState)?.Value || '200');
    const readmoreLinkText = getConfigValue(appState?.config?.AnnouncementManagement?.Carousel?.ReadMoreLink?.Label, appState)?.Value || 'more';
    const [isPaused, setIsPaused] = useState(false);
    const { showBoundary } = useErrorBoundary();

    const hasMultipleAnnouncements = announcements.length > 1;

    const [showModal, setShowModal] = useState(false);
    const [modalHeader, setModalHeader] = useState('');
    const [modalContent, setModalContent] = useState('');

    const handleShowModal = (productName, content) => {
        setModalHeader(productName);
        setModalContent(content);
        setShowModal(true);
        setIsPaused(true);
    };

    useEffect(() => {
        if (cardEnabled) {
            getAnnouncementData();
        }
    }, []);

    const getAnnouncementData = async () => {
        await getAnnoucements(announcmentPartition)
            .then((responseJson) => {
                const activeAnnouncements = responseJson.filter(a => a.isActive).map(a => ({
                    ...a,
                    content: DOMPurify.sanitize(a.content)
                }));
                setAnnouncements(activeAnnouncements);
                setAnnouncementsEnabled(activeAnnouncements.length> 0);
                setIsLoading(false);
            })
            .catch((err) => {
                showBoundary(err);
                setIsLoading(false);
            });
    }

     function getProduct(productId) {
        if (!productId) {
            return announcementAllBrandLabel;
        }
        
        return appState?.productDetails.find(x => x.id === productId).name;
    }   

    return (
        <>
            {(cardEnabled && announcementsEnabled) && 
                <>
                    <Container className="mt-5 card">
                        <Row className="justify-content-center align-items-center">
                            <Col md={12}>
                                <Carousel variant="dark" indicators={hasMultipleAnnouncements} controls={hasMultipleAnnouncements} interval={isPaused ? null : slideTransitionTime}>
                                    {announcements.map((item) => (
                                        <Carousel.Item key={item.productId}>
                                            <div className="d-flex flex-column align-items-center justify-content-center offset-2 col-8" style={{ minHeight: '200px', maxHeight: '325px', borderRadius: '15px' }}>
                                                <AnnouncementSlide productName={getProduct(item.productId)} 
                                                    content={item.content} 
                                                    maxLength={maximumCharacterCount} 
                                                    readMoreText={readmoreLinkText} 
                                                    onReadMore={() => handleShowModal(getProduct(item.productId), item.content)} />
                                            </div>
                                        </Carousel.Item>
                                    ))}
                                </Carousel>
                            </Col>
                        </Row>
                    </Container>

                    <AnnouncementModal show={showModal} setShow={setShowModal} productName={modalHeader} content={modalContent} setIsPaused={setIsPaused}/>            
                </>       
            }
        </>
    );
}

export default AnouncementsCard;
